import {
  type PropsWithChildren,
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

import {
  type FitifyInputEventListener,
  addInputEventListener,
  FitifyInputEvent,
  removeInputEventListener,
} from '../utils/form-events'

type ContextProps = {
  focusedInputUuid?: string
  isSomeInputMounted: boolean
}

const Context = createContext<ContextProps>({
  isSomeInputMounted: false,
})

export const FormInputContextProvider = ({ children }: PropsWithChildren) => {
  const [mountedInputUuids, setMountedInputUuids] = useState<string[]>([])
  const [focusedInputUuid, setFocusedInputUuid] = useState<string | undefined>(
    undefined
  )

  useLayoutEffect(() => {
    const listener: FitifyInputEventListener = (event) => {
      if (event?.event === FitifyInputEvent.Mount) {
        setMountedInputUuids((prev) => {
          if (!prev.includes(event.inputUuid)) {
            return [...prev, event.inputUuid]
          }
          return prev
        })
      }
      if (event?.event === FitifyInputEvent.Focus) {
        if (mountedInputUuids.includes(event.inputUuid)) {
          setFocusedInputUuid(event.inputUuid)
        }
      }
      if (
        event?.event === FitifyInputEvent.Blur ||
        event?.event === FitifyInputEvent.Unmount
      ) {
        setFocusedInputUuid((prev) =>
          prev === event.inputUuid ? undefined : prev
        )
      }
      if (event?.event === FitifyInputEvent.Unmount) {
        setMountedInputUuids((prev) =>
          prev.filter((uuid) => uuid !== event.inputUuid)
        )
      }
    }
    addInputEventListener(listener)

    return () => {
      removeInputEventListener(listener)
    }
  }, [mountedInputUuids])

  const value = useMemo(() => {
    return {
      focusedInputUuid,
      isSomeInputMounted: Boolean(mountedInputUuids.length),
    }
  }, [focusedInputUuid, mountedInputUuids.length])

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const useFormInputContext = () => {
  return useContext(Context)
}
